import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Download, MessageCircle, Crown } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import LegalFooter from '../components/LegalFooter';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * /quasi — "Quasi vinta" hidden landing.
 *
 * Single image surrounded by a "text above" and "text below". Designed so the
 * admin can upload a screenshot of a multiple ticket that lost by very little,
 * pair it with a short caption, and push it to all users. Users can DOWNLOAD
 * the image for re-sharing on social/WhatsApp (the whole conversion idea).
 *
 * Visual: identical brand block, ambra accents, and ADM legal footer as
 * /book and /maggiorate so it feels native to the app.
 */
const NearMissLandingPage = ({ endpoint = 'near-miss', slug = 'quasi', fileName = 'quasi-vinta-rvincente', alt = 'Quasi vinta' }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // /win opened from a push (?pn=<notification_id>): count the open, remember it for the chat tap
  useEffect(() => {
    if (slug !== 'win') return;
    const pn = new URLSearchParams(location.search).get('pn');
    if (!pn) return;
    let anonId = null;
    try {
      sessionStorage.setItem('win_pn', pn);
      anonId = localStorage.getItem('rv_anon_id');
      if (!anonId) { anonId = `a${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`; localStorage.setItem('rv_anon_id', anonId); }
    } catch {}
    axios.post(`${API}/landing/win/push-open`, { notification_id: pn, anon_id: anonId }).catch(() => {});
  }, [slug, location.search]);

  const openChat = async () => {
    const target = '/chat?da=email';
    let pn = null;
    try { pn = sessionStorage.getItem('win_pn'); } catch {}
    try {
      const r = await axios.post(`${API}/landing/win/cta-tap`, { notification_id: pn }, { timeout: 3000 });
      if (!user && r.data?.tap_id) localStorage.setItem('win_tap_pending', r.data.tap_id);
    } catch {}
    if (!user) {
      try { localStorage.setItem('post_login_redirect', JSON.stringify({ path: target, ts: Date.now() })); } catch {}
    }
    navigate(target);
  };

  // Chat with the VIP request already typed in (user taps Invia); VIP members go straight to the area
  const openVip = () => {
    if (user?.is_vip) { navigate('/vip'); return; }
    const target = '/chat?vip=request';
    if (!user) {
      try { localStorage.setItem('post_login_redirect', JSON.stringify({ path: target, ts: Date.now() })); } catch {}
    }
    navigate(target);
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/landing/${endpoint}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Errore nel caricamento');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [endpoint]);

  const downloadImage = async () => {
    if (!data?.image_url) return;
    setDownloading(true);
    try {
      // Fetch as blob → forces a real "Save as" dialog instead of just opening
      // the image in a new tab (some mobile browsers do that with anchor download).
      const resp = await fetch(data.image_url, { credentials: 'omit' });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Best-guess extension from MIME so the file opens correctly after download
      const ext = (blob.type.split('/')[1] || 'jpg').split('+')[0];
      a.download = `${fileName}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      // Fallback: open the image URL in a new tab so the user can long-press to save
      window.open(data.image_url, '_blank', 'noopener');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <p className="text-red-400 text-sm">{error || 'Contenuto non disponibile'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F0F0F] via-[#171717] to-[#0F0F0F] text-white">
      <div className="max-w-md mx-auto px-5 pt-10 pb-4" data-testid={`landing-${slug}`}>
        {/* Brand */}
        <div className="flex items-center justify-center gap-2 mb-7" data-testid="landing-brand">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
            <span className="text-black font-black text-sm tracking-tighter">R</span>
          </div>
          <span className="font-black text-base tracking-[0.25em] text-amber-400">VINCENTE</span>
        </div>

        {/* Text above image */}
        {data.text_above && (
          <p
            className="text-center text-gray-200 text-base font-bold leading-snug mb-5 whitespace-pre-wrap"
            data-testid={`${slug}-text-above`}
          >
            {data.text_above}
          </p>
        )}

        {/* Image card */}
        {data.image_url ? (
          <div
            className="relative rounded-2xl overflow-hidden border-2 border-amber-400/60 shadow-[0_0_38px_-12px_rgba(245,166,35,0.75)] bg-[#0a0a0a]"
            data-testid={`${slug}-image-wrapper`}
          >
            <img
              src={data.image_url}
              alt={alt}
              className="w-full h-auto block"
              loading="eager"
              data-testid={`${slug}-image`}
            />
          </div>
        ) : (
          <div
            className="rounded-2xl border border-dashed border-gray-700 bg-[#171717] py-12 flex items-center justify-center text-gray-500 text-sm"
            data-testid={`${slug}-image-placeholder`}
          >
            Nessuna immagine caricata
          </div>
        )}

        {/* Download CTA */}
        {data.image_url && (
          <button
            onClick={downloadImage}
            disabled={downloading}
            className="w-full mt-5 py-3 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-black font-black text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60"
            data-testid={`${slug}-download-btn`}
          >
            {downloading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Scarico…
              </>
            ) : (
              <>
                <Download className="w-4 h-4" /> Scarica immagine
              </>
            )}
          </button>
        )}

        {/* Text below image */}
        {data.text_below && (
          <p
            className="text-center text-gray-300 text-sm leading-relaxed mt-6 whitespace-pre-wrap"
            data-testid={`${slug}-text-below`}
          >
            {data.text_below}
          </p>
        )}

        {slug === 'win' && data.chat_cta_text && (
          <button
            onClick={openChat}
            className="w-full mt-6 py-4 rounded-2xl border-2 border-amber-400/70 bg-amber-400/10 text-amber-300 font-black text-base flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            data-testid="win-chat-cta-btn"
          >
            <MessageCircle className="w-5 h-5" /> {data.chat_cta_text}
          </button>
        )}

        {slug === 'quasi' && (
          <button
            onClick={openVip}
            className="w-full mt-6 py-4 rounded-2xl border-2 border-amber-400/70 bg-amber-400/10 text-amber-300 font-black text-base flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            data-testid="quasi-vip-cta-btn"
          >
            <Crown className="w-5 h-5" /> Scopri l'area VIP
          </button>
        )}

        {/* Footer legale ADM */}
        <LegalFooter />
      </div>
      {/* App bottom navigation */}
      <BottomNav />
    </div>
  );
};

export default NearMissLandingPage;
